import React from 'react';
import { Container, Typography, Grid, Paper, Box, Card, CardContent, Button, Stack, Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import BusinessIcon from '@mui/icons-material/Business';
import LaunchIcon from '@mui/icons-material/Launch';
import HandshakeIcon from '@mui/icons-material/Handshake';
import VerifiedIcon from '@mui/icons-material/Verified';

export default function Partners() {
  const partners = [
    { 
      name: 'Bar Association of Vridhachalam', 
      url: 'https://bav-advocates.onrender.com/', 
      logo: '/partners/partner1.jpg',
      category: 'Legal Services',
      description: 'Professional legal services and advocacy platform for the Bar Association community.',
      services: ['Digital Platform', 'Member Management', 'Case Tracking']
    },
    { 
      name: 'RheinBrücke IT Consulting', 
      url: 'https://www.rheincs.com', 
      logo: '/partners/partner2.png',
      category: 'Technology Partner',
      description: 'Leading IT consulting firm specializing in SAP, enterprise solutions, and digital transformation.',
      services: ['SAP Solutions', 'Cloud Services', 'Digital Consulting']
    },
    { 
      name: 'Sri Venkateshwara Specialty Clinic, Vridhachalam', 
      url: '', 
      logo: '/partners/srivenkateswarahospital.png',
      category: 'Healthcare',
      description: 'Multi-specialty healthcare clinic providing comprehensive medical services to the community.',
      services: ['Healthcare IT', 'Patient Management', 'Digital Health']
    }
  ];

  const stats = [
    { number: '3+', label: 'Active Partners' },
    { number: '5+', label: 'Years Partnership' },
    { number: '100%', label: 'Success Rate' }
  ];

  return (
    <Box sx={{ 
      background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.02) 0%, rgba(118, 75, 162, 0.02) 100%)',
      minHeight: '100vh',
      py: 8
    }}>
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box sx={{ 
          textAlign: 'center', 
          mb: 6,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -40,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 80,
            height: 4,
            background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 2
          }
        }}>
          <Box sx={{
            display: 'inline-block',
            mb: 2
          }}>
            <Box sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
              boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
            }}>
              <HandshakeIcon sx={{ fontSize: '2.5rem', color: 'white' }} />
            </Box>
          </Box>

          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 800, 
              mb: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            Our Trusted Partners
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'text.secondary', 
              maxWidth: 800, 
              mx: 'auto',
              lineHeight: 1.8,
              fontWeight: 400,
              mb: 5
            }}
          >
            We collaborate with leading technology and business partners to deliver integrated, innovative solutions 
            that drive digital transformation and create lasting value for our clients.
          </Typography>

          {/* Stats */}
          <Grid container spacing={3} sx={{ maxWidth: 800, mx: 'auto', mb: 6 }}>
            {stats.map((stat, idx) => (
              <Grid item xs={12} sm={4} key={idx}>
                <Box sx={{ 
                  p: 3, 
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                  border: '1px solid',
                  borderColor: alpha('#667eea', 0.2),
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.2)'
                  }
                }}>
                  <Typography variant="h3" sx={{ 
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 0.5
                  }}>
                    {stat.number}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Partners Grid */}
        <Grid container spacing={4}>
          {partners.map((p, i) => (
            <Grid item xs={12} md={4} key={i}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 40px rgba(102, 126, 234, 0.2)',
                    borderColor: alpha('#667eea', 0.3)
                  }
                }}
              >
                {/* Logo Section with Gradient Background */}
                <Box sx={{
                  position: 'relative',
                  p: 4,
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 180,
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}>
                  {p.logo ? (
                    <Box 
                      component="img" 
                      src={p.logo} 
                      alt={p.name} 
                      sx={{ 
                        maxWidth: '100%', 
                        maxHeight: 120, 
                        objectFit: 'contain',
                        filter: 'grayscale(0%)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          filter: 'grayscale(0%)',
                          transform: 'scale(1.05)'
                        }
                      }} 
                    />
                  ) : (
                    <BusinessIcon sx={{ fontSize: 80, color: alpha('#667eea', 0.3) }} />
                  )}
                  
                  {/* Verified Badge */}
                  <Box sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '50%',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                  }}>
                    <VerifiedIcon sx={{ fontSize: 20, color: 'white' }} />
                  </Box>
                </Box>

                <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
                  {/* Category Chip */}
                  <Chip 
                    label={p.category} 
                    size="small" 
                    sx={{ 
                      mb: 2,
                      alignSelf: 'flex-start',
                      background: alpha('#667eea', 0.1),
                      color: '#667eea',
                      fontWeight: 600,
                      borderRadius: 2
                    }} 
                  />

                  {/* Partner Name */}
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 700, 
                      mb: 1.5,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      lineHeight: 1.3
                    }}
                  >
                    {p.name}
                  </Typography>

                  {/* Description */}
                  {p.description && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary', 
                        mb: 2,
                        lineHeight: 1.7,
                        flexGrow: 1
                      }}
                    >
                      {p.description}
                    </Typography>
                  )}

                  {/* Services */}
                  {p.services && (
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {p.services.map((service, idx) => (
                        <Chip 
                          key={idx}
                          label={service} 
                          size="small"
                          sx={{ 
                            fontSize: '0.7rem',
                            height: 24,
                            backgroundColor: alpha('#667eea', 0.05),
                            color: 'text.secondary',
                            border: '1px solid',
                            borderColor: alpha('#667eea', 0.2)
                          }}
                        />
                      ))}
                    </Stack>
                  )}

                  {/* Visit Website Button */}
                  {p.url ? (
                    <Button
                      fullWidth
                      variant="contained"
                      endIcon={<LaunchIcon />}
                      component="a"
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        mt: 'auto',
                        py: 1.5,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                          boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                          transform: 'translateY(-2px)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Visit Website
                    </Button>
                  ) : (
                    <Box sx={{ 
                      mt: 'auto',
                      py: 1.5, 
                      textAlign: 'center',
                      borderRadius: 2,
                      background: alpha('#667eea', 0.05),
                      border: '1px solid',
                      borderColor: alpha('#667eea', 0.2)
                    }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        Trusted Partner
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* CTA Section */}
        <Box sx={{ 
          mt: 8, 
          p: 6, 
          borderRadius: 4,
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)',
          border: '1px solid',
          borderColor: alpha('#667eea', 0.2),
          textAlign: 'center'
        }}>
          <Box sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
            boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)'
          }}>
            <HandshakeIcon sx={{ fontSize: '2rem', color: 'white' }} />
          </Box>
          
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            Become a Partner
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, maxWidth: 600, mx: 'auto', lineHeight: 1.8 }}>
            Join our partner ecosystem and collaborate with us to deliver innovative solutions. 
            Together, we can create exceptional value for our clients and drive digital transformation.
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            href="/contact"
            sx={{
              py: 1.5,
              px: 4,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                boxShadow: '0 6px 24px rgba(102, 126, 234, 0.4)',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Partner With Us
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
